import { Button, Divider } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import BigNumber from 'bignumber.js'
import classNames from 'classnames'
import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import * as Scroll from 'react-scroll'
import styles from './BasicInfo.module.scss'
import UpSellContext from '../../../context/UpSellContext'
import useCart from '../../../hooks/cart'
import useClubContext from '../../../hooks/club/clubContext'
import useAppEvents from '../../../hooks/events/events'
import { AppEventsEnum } from '../../../hooks/events/events.types'
import useProduct from '../../../hooks/product/product'
import ChannelInterface from '../../../interfaces/ChannelInterface'
import ProductInterface, { PANEL_KIND, SIZE_PRESET_NONE } from '../../../interfaces/ProductInterface'
import { State } from '../../../services/reducers'
import { AxiosError, AxiosResponse } from '../../../utils/axios'
import { getProductName } from '../../../utils/catalog'
import { getProductLink } from '../../../utils/link'
import { _at } from '../../../utils/translations'
import Asset from '../../Asset'
import FormattedMessage from '../../FormattedMessage'
import notify from '../../Notification/notify'
import Price from '../../Price/Price'
import SocialIcons from '../../SocialIcons/SocialIcons'
import ProductDetails from '../Details'
import Gallery from '../Gallery'
import ProductVariant from '../ProductVariant/ProductVariant'

type Props = {
  channel: ChannelInterface
  product?: ProductInterface
  classes?: {
    root?: string
  }
  sizePanelId?: string
  hasValidSizePreset?: boolean
}

const BasicInfo = (props: Props) => {
  const _product = props.product

  const { classes, channel, sizePanelId, hasValidSizePreset } = props
  const {
    product,
    setProduct,
    setVariantId,
    setQuantity,
    variantId,
    getVariant,
    getSizes,
    getAvailability,
    getPrice,
    getFeatures,
    quantity,
  } = useProduct()
  const upSell = useContext(UpSellContext)
  const clubContext = useClubContext()
  const [mobileSizeGuideActive, setMobileSizeGuideActive] = useState<boolean>(false)
  const [addingToCart, setAddingToCart] = useState<boolean>(false)
  const isNoneSize = _product?.variants[0]?.size?.preset === SIZE_PRESET_NONE
  const currency = useSelector((state: State) => state.locale.currency)
  const { addProduct, adjustProduct, cart } = useCart()
  const { triggerEvent } = useAppEvents()
  const loyaltyPointsGainRate = useSelector((state: State) => state.global.loyaltyPointsGainRate)
  const showNoSizeAlert = () => {
    notify.error(<FormattedMessage id="BasicInfo.NoSizeAlert" description="BasicInfo.NoSizeAlert" defaultMessage="Please select a size" />)
  }

  useEffect(() => {
    if (_product) {
      setProduct(_product)
      triggerEvent(AppEventsEnum.VIEW_ITEM, { product: _product, currency: currency.isoCode })
    }
  }, [])

  useEffect(() => {
    if (product && isNoneSize && !variantId) {
      setVariantId(product?.variants[0]?.id!)
    }
  }, [product ? product?.id : product, cart?.id])

  if (!_product) {
    return (
      <div className={classNames(styles['basic'], classes?.root!)}>
        <Skeleton variant="rectangular" classes={{ root: styles['basic-info__gallery--skeleton'] }} />

        <Skeleton variant="rectangular" classes={{ root: styles['basic-info--skeleton'] }} />
      </div>
    )
  }

  const addProductToCart = () => {
    setAddingToCart(true)
    if (!variantId || !product) {
      return
    }
    let qty = 1

    const foundProduct = cart?.items.find(cartItem => cartItem.product.id === (variantId || product?.variants[0]?.id!))
    qty = foundProduct ? foundProduct.quantity + 1 : 1

    addProduct(
      {
        productId: variantId!,
        quantity: qty,
      },
      (response: AxiosResponse): void => {
        setAddingToCart(false)
        setQuantity(quantity + 1)
        triggerEvent(AppEventsEnum.ADJUST_CART, { adjQty: 1, currency: currency.isoCode, product, variantId })
        upSell.handleOpen(_product.id)
      },
      (error: AxiosError) => {
        setAddingToCart(false)
      },
      false,
      true,
    )
  }

  const onAddProduct = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    variantId ? addProductToCart() : showNoSizeAlert()
  }

  const variant = getVariant()
  const sizeList = getSizes()
  const availability = getAvailability(variant || product)
  const available = availability?.available || clubContext.isInClubFlow
  const productSlug = _at('slug', _product?.description.translations!)
  const shareUrl = encodeURIComponent(`https://${channel.canonicalDomain}${getProductLink(_product?.id, productSlug)}`)
  const shareImage = _product?.description.photos.product ? _product?.description.photos.product[0].url! : '/img/not_found.jpg'
  const printName = _at('printName', _product?.translations!)
  const styleCategory = _at('styleCategory', _product?.translations!)
  const styleName = _at('styleName', _product?.translations!)
  const styleType = _at('styleType', _product?.translations!)
  const name = getProductName(styleCategory, styleName, styleType)
  const features = getFeatures().slice(0, 2)

  const sizeGuideLink = hasValidSizePreset ? (
    <Scroll.Link
      to={sizePanelId!}
      className={styles['basic-info__size-guide-link']}
      smooth="easeInQuad"
      duration={800}
      onClick={() => setMobileSizeGuideActive(true)}
    >
      <FormattedMessage id="BasicInfo.SizeGuideLink" description="BasicInfo.SizeGuideLink" defaultMessage="Size Guide" />
    </Scroll.Link>
  ) : null

  const gainPointsValue = new BigNumber(getPrice()?.grossPrice!)
    .multipliedBy(100)
    .multipliedBy(loyaltyPointsGainRate)
    .dividedBy(100)
    .toFixed(0, BigNumber.ROUND_DOWN)

  return (
    <div className={classNames(styles['basic'], classes?.root!)}>
      <Gallery product={_product} price={getPrice()} />
      <div className={styles['basic-box']}>
        <div className={styles['basic-info']}>
          <h1 className={styles['basic-info__product-name']}>{printName}</h1>
          <h2 className={styles['basic-info__category-name']}>{name}</h2>

          <Price
            value={getPrice()?.grossPrice!}
            saleValue={getPrice()?.salePriceGross}
            classes={{
              root: styles['basic-info__price'],
              sale: styles['basic-info__price--sale'],
              oldValue: styles['basic-info__price--old'],
            }}
          />

          <div className={styles['basic-info__product-features']}>
            {features.map(feature => (
              <div key={`product-feature-${feature.id}`} className={styles['product-features__feature']}>
                {feature.stringValue}
              </div>
            ))}
          </div>

          <div
            className={styles['basic-info__description']}
            dangerouslySetInnerHTML={{ __html: _at('descriptionShort', _product!.description?.translations) }}
          />

          <Divider classes={{ root: classNames('desktop--hidden', styles['basic-info__divider']) }} />
          {available && product && (clubContext.isInClubFlow || product!?.breadcrumbs?.length > 0) ? (
            <>
              {isNoneSize ? null : (
                <>
                  <ProductVariant
                    classes={{ root: styles['basic-info__variants'] }}
                    sizes={sizeList}
                    variantId={variantId}
                    setVariantId={setVariantId}
                    addingToCart={addingToCart}
                    getVariant={getVariant}
                  />
                  {sizeGuideLink}
                </>
              )}

              <Button
                color="primary"
                variant="contained"
                classes={{ root: styles['basic-info__button'] }}
                disabled={addingToCart || !variantId}
                onClick={onAddProduct}
                size="large"
              >
                <FormattedMessage id="BasicInfo.AddToBag" description="BasicInfo.AddToBag" defaultMessage="Add to bag" />
              </Button>
            </>
          ) : (
            <div className={styles['basic-info__no-available']}>
              <FormattedMessage
                id="BasicInfo.NoAvailable"
                description="BasicInfo.NoAvailable"
                defaultMessage="This product is{br}no longer available"
                values={{
                  br: <br />,
                }}
              />
            </div>
          )}
          <SocialIcons shareMode shareUrl={shareUrl} shareImageUrl={shareImage} classes={{ root: styles['basic-info__social'] }} />
          {available && product && !clubContext.isInClubFlow && product!?.breadcrumbs?.length > 0 ? (
            <div className={styles['basic-info__ftf']}>
              <Asset file="img/ftf_logo.png" type="image" withoutTheme classes={styles['ftf__logo']} />
              <FormattedMessage
                id="BasicInfo.FTFGainPoints"
                defaultMessage="By purchasing this product you can collect up to {points} loyalty points with your Feed The Funkys account."
                values={{
                  points: gainPointsValue,
                }}
              />
            </div>
          ) : null}
        </div>
        <ProductDetails
          product={product || _product}
          isNoneSize={isNoneSize}
          sizePanelId={sizePanelId}
          onSizeGuideHide={() => setMobileSizeGuideActive(false)}
          activePanel={mobileSizeGuideActive ? PANEL_KIND.SIZE_PANEL : PANEL_KIND.DESCRIPTION}
          hasValidSizePreset={hasValidSizePreset || false}
        />
      </div>
    </div>
  )
}

export default BasicInfo
